import { useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useFieldArray, useFormContext } from "react-hook-form";

import { ReactComponent as TrashIcon } from "assets/images/iconsV2/trash-01.svg";
import Button from "components/atoms/Button/Button";
import ButtonV2 from "components/v2/Button/Button";
import { StopTimeType, StopType } from "types/MultiStop";
import DropAddressForm from "./DropAddressForm";
import DropContactForm from "./DropContactForm";
import DropPlanningForm from "./DropPlanningForm/DropPlanningForm";
import FormHr from "../../Common/FormHr";

interface DropValue {
  address_id?: number;
  stop_type: StopType; // PICKUP = 1 DROPOFF = 2
  stop_time_type: StopTimeType; // BEFORE = 1, AFTER = 2, BETWEEN = 3
  stop_address_type: string;
  stop_contact_name?: string;
  stop_contact_number?: string;
  stop_contact_email?: string;
  stop_after_date?: string | null; // always present for BEFORE, AFTER and BETWEEN (in timezone of the address)
  stop_after_time?: string; // always present for BEFORE, AFTER and BETWEEN (in timezone of the address)
  stop_before_date?: string | null; //  present in BETWEEN (in timezone of the address)
  stop_before_time?: string | null; //  present in BETWEEN (in timezone of the address)
  stop_notes?: string;
  stop_hours_start?: string;
  stop_hours_end?: string;
}

const defaultDropValue: DropValue = {
  stop_type: StopType.DROPOFF, // PICKUP = 1 DROPOFF = 2
  stop_time_type: StopTimeType.BEFORE, // BEFORE = 1, AFTER = 2, BETWEEN = 3
  stop_address_type: "address",
  stop_contact_name: "",
  stop_contact_number: undefined,
  stop_contact_email: undefined,
  stop_after_date: null,
  stop_after_time: undefined,
  stop_before_date: null,
  stop_before_time: null,
  stop_notes: "",
};

export default function MultiDropForm({ contactBook }: { contactBook?: any[] }) {
  const intl = useIntl();
  const { fields, append, remove } = useFieldArray({
    name: "schedule.stops",
  });
  const { setValue } = useFormContext();

  useEffect(() => {
    if (fields.length >= 3) return;
    if (fields.length === 0) {
      setValue("schedule", {});
      // For first stop, set different default values
      append({
        ...defaultDropValue,
        stop_type: StopType.PICKUP, // Default: PICKUP
        stop_time_type: StopTimeType.AFTER, // Default: AFTER
      });
      return;
    }
    append(defaultDropValue);
  }, [fields, append]);

  return (
    <>
      {fields.map((item, index) => (
        <div key={item.id}>
          <div className="d-flex justify-content-between mt-3xl">
            <div className="text-md text-tertiary font-weight-semibold">
              {index === 0 ? (
                <FormattedMessage id="general.pickup" defaultMessage="Pickup" />
              ) : (
                <FormattedMessage
                  id="new-request.schedule.deliveryStop"
                  defaultMessage="Delivery stop #{stopNumber}"
                  values={{ stopNumber: index }}
                />
              )}
            </div>
            {index >= 3 && (
              <ButtonV2
                label={intl.formatMessage({
                  id: "new-request.schedule.removeStop",
                  defaultMessage: "Remove stop",
                })}
                labelClassName="text-sm"
                iconLeft={<TrashIcon />}
                variant="link-error"
                size="sm"
                onClick={() => remove(index)}
              />
            )}
          </div>
          <DropAddressForm index={index} />
          <DropContactForm index={index} contactBook={contactBook} />
          <DropPlanningForm index={index} />
          <FormHr />
        </div>
      ))}

      <Button
        className="rounded-pill my-3xl"
        onClick={() => append(defaultDropValue)}
        label={intl.formatMessage({
          id: "new-request.schedule.addNewStop",
          defaultMessage: "Add new stop",
        })}
      />
    </>
  );
}

MultiDropForm.defaultProps = {
  contactBook: [],
};
